<script lang="ts">
  // Charleston (home): hero strip → area picker → top races (+ desk notes) → precinct map → local races in tabs.
  import type { Overview, RaceGroup, RaceSummary } from '../../types';
  import { app, ds, pollPhase, resource } from '../../lib/app.svelte';
  import { fmtCompact, fmtInt, fmtPct, fmtTime } from '../../lib/format';
  import { anchorRace, counterpartId, GROUP_ORDER, isPreResults, leftToCount } from '../../lib/race';
  import { isPseudo, type MapMode } from '../../lib/geo';
  import HeadToHead from '../parts/HeadToHead.svelte';
  import RaceCard from '../parts/RaceCard.svelte';
  import RaceRows from '../parts/RaceRows.svelte';
  import AreaPicker from '../parts/AreaPicker.svelte';
  import Notes from '../parts/Notes.svelte';
  import ResultsMap from '../parts/ResultsMap.svelte';
  import Ring from '../atoms/Ring.svelte';
  import Est from '../atoms/Est.svelte';
  import EarlyVote from '../parts/EarlyVote.svelte';
  import Lazy from '../atoms/Lazy.svelte';
  import ReplayCta from '../parts/ReplayCta.svelte';
  import Countdown from '../parts/Countdown.svelte';

  let { ov }: { ov: Overview | null } = $props();

  const chs = $derived((ov?.races ?? []).filter((r) => r.scope === 'charleston'));
  const anchor = $derived(anchorRace(chs));
  const anchorFile = resource(() => (anchor && app.shownSid ? ds().race(app.shownSid, anchor.race_id) : null));
  const notes = resource(() => { void app.meta?.notes_updated; return ds().notes(); });
  const ev = resource(() => { void app.liveSid; return ds().earlyVote(); });
  /** before any votes are counted, early voting is the only real data */
  const pre = $derived(isPreResults(app.meta, ov ? chs : null));
  const waiting = $derived(!ov && !pre);
  /** live 2026 before the first votes: countdown to 7 PM, then "polls closed — waiting" */
  const phase = $derived(pollPhase(ov));

  /** the remembered area only applies if this dataset has it (e.g. picked in the 2024 replay, then back to 2026) */
  const area = $derived(app.area && ov && !ov.areas.some((a) => a.area_id === app.area) ? null : app.area);
  const matches = (r: RaceSummary) => !area || r.scope === 'statewide' || r.areas.includes(area);
  const used = $derived(new Set(chs.flatMap((r) => r.areas)));
  const isTop = (r: RaceSummary) => r.status !== 'uncontested' && (r.group === 'statewide' || r.group === 'congress' || (r.group === 'referendum' && !!anchor && r.units_total >= 0.9 * anchor.units_total));
  /** big head-to-head cards: top of the ticket (Governor / President, U.S. Senate), U.S. House, countywide referendums.
   *  The other statewide offices go in a compact grid so the phone page isn't 11 big cards long. */
  const isBig = (r: RaceSummary) => /-(gov|pres|ussen)$/.test(r.race_id) || r.group === 'congress' || r.group === 'referendum';
  const rank = (r: RaceSummary) => (r.race_id === anchor?.race_id ? -1 : GROUP_ORDER.indexOf(r.group));
  const topAll = $derived(chs.filter((r) => isTop(r) && matches(r)).sort((a, b) => rank(a) - rank(b)));
  const top = $derived(topAll.filter(isBig));
  const otherState = $derived(topAll.filter((r) => !isBig(r)));
  const altOf = (r: RaceSummary) => (ov?.races ?? []).find((x) => x.race_id === counterpartId(r.race_id)) ?? null;

  // ---- local races in tabs
  type Tab = 'house' | 'council' | 'school' | 'ref' | 'other';
  const TAB_OF = (g: RaceGroup): Tab => (g === 'state_house' ? 'house' : g === 'county_council' ? 'council' : g === 'school_board' ? 'school' : g === 'referendum' ? 'ref' : 'other');
  const local = $derived(chs.filter((r) => !isTop(r) && r.status !== 'uncontested' && matches(r)));
  const unopposed = $derived(chs.filter((r) => r.status === 'uncontested' && matches(r)).sort((a, b) => GROUP_ORDER.indexOf(a.group) - GROUP_ORDER.indexOf(b.group)));
  const hasSenate = $derived(local.some((r) => r.group === 'state_house' && /senate/i.test(r.title)));
  const tabs = $derived(([['house', hasSenate ? 'Legislature' : 'State House'], ['council', 'County Council'], ['school', 'School Board'], ['ref', 'Referendums'], ['other', 'Other']] as [Tab, string][])
    .map(([id, label]) => ({ id, label, races: local.filter((r) => TAB_OF(r.group) === id) })).filter((t) => t.races.length));
  let tab = $state<Tab>('house');
  /** WAI-ARIA tabs: ←/→/Home/End move between tabs (roving tabindex) */
  function onTabKey(e: KeyboardEvent) {
    const i = tabs.findIndex((t) => t.id === activeTab?.id);
    let j = i;
    if (e.key === 'ArrowRight') j = (i + 1) % tabs.length;
    else if (e.key === 'ArrowLeft') j = (i - 1 + tabs.length) % tabs.length;
    else if (e.key === 'Home') j = 0;
    else if (e.key === 'End') j = tabs.length - 1;
    else return;
    e.preventDefault();
    tab = tabs[j]!.id;
    (document.getElementById(`tab-${tab}`) as HTMLElement | null)?.focus();
  }
  const activeTab = $derived(tabs.find((t) => t.id === tab) ?? tabs[0]);

  // ---- map
  let mapRaceId = $state<string | null>(null);
  let mapMode = $state<MapMode>('margin');
  const mapChoices = $derived(chs.filter((r) => isTop(r) || r.race_id === mapRaceId));
  const mapRace = $derived(chs.find((r) => r.race_id === mapRaceId) ?? anchor);
  let mapNear = $state(false); // the map card is below the fold: load race + geometry only when it is close
  const mapFile = resource(() => (mapNear && mapRace && app.shownSid ? ds().race(app.shownSid, mapRace.race_id) : null));
  const geo = resource(() => (mapNear && mapFile.value?.geo ? ds().geoLayer(mapFile.value.geo.layer) : null));

  // ---- hero numbers (anchor race, Charleston)
  const af = $derived(anchorFile.value && anchorFile.value.race_id === anchor?.race_id ? anchorFile.value : null);
  const bt = (t: string) => af?.by_type.find((b) => b.vote_type === t);
  const left = $derived(af && af.total_votes.projected ? leftToCount(af) : null);
  const edayIn = $derived(af ? af.units.filter((u) => !isPseudo(u.unit_id) && u.total_by_type.eday > 0).length : 0);
  const realUnits = $derived(af ? af.units.filter((u) => !isPseudo(u.unit_id)).length : 0);
  const statusLine = $derived.by(() => {
    if (!af) return '';
    const e = bt('early');
    const parts: string[] = [];
    if (af.total_votes.counted === 0) return 'Polls close at 7 PM. The first results usually arrive within the hour.';
    if (e && e.counted > 0) parts.push(e.remaining && e.remaining.median === 0 ? 'Early votes are in' : 'Early votes are being counted');
    parts.push(`Election Day precincts reporting: ${edayIn} of ${realUnits}`);
    return parts.join(' · ');
  });
  const tiles = $derived([
    { k: 'Votes counted', v: af?.total_votes.counted ?? null, est: false },
    { k: 'Early in person', v: bt('early')?.counted ?? null, est: false },
    { k: 'Absentee by mail', v: bt('mail')?.counted ?? null, est: false },
    { k: 'Election Day', v: bt('eday')?.counted ?? null, est: false },
  ]);
</script>

<div class="wrap">
  <a class="vvv" href={app.href('/vote')}>Vote Vote Vote <span aria-hidden="true">▶</span></a>
  <section class="head" class:withcd={!!phase}>
    <div>
      <div class="kicker">{ov?.election.name ?? app.meta?.election.name ?? ''}</div>
      <h1>Charleston County</h1>
      <p class="lede">{phase === 'closed' ? 'Polls are closed. Results start arriving soon.' : pre || phase ? (app.replay ? `Polls just closed — ${fmtTime(ov?.generated_at ?? null)}. The first results arrive within the hour.` : app.preClose ? 'Results start after polls close at 7 PM.' : 'Polls are closed. Charleston County’s first results usually arrive within the hour.') : `${app.isLive ? (app.final ? 'Final results' : 'Live results') : 'Replay'} · as of ${fmtTime(ov?.generated_at ?? null)}`}</p>
    </div>
    {#if phase && app.pollsClose !== null}<Countdown {phase} close={app.pollsClose} now={app.tick} />{/if}
  </section>

  <ReplayCta />

  <!-- 1. hero: early voting before results, counting progress after -->
  {#if waiting}
    <div class="skel first hero-skel"></div>
  {:else if pre}
    {#if ev.value}<div class="section first"><EarlyVote ev={ev.value} scope="charleston" variant="hero" /></div>{/if}
  {:else}
  <section class="card hero" aria-label="How much is counted">
    <div class="ringbox">
      <Ring value={anchor?.pct_expected_counted?.median ?? null} size={96} stroke={9} est label="Share of the expected vote counted (estimate)" />
      <div>
        <div class="kline"><span class="kicker est">Expected vote counted</span><Est /></div>
        <div class="ringtxt">{anchor?.pct_expected_counted ? `About ${fmtPct(anchor.pct_expected_counted.median, 0)}` : 'Waiting for results'}</div>
        <div class="muted small">{anchor ? `${anchor.title}, Charleston County` : ''}{anchor?.pct_expected_counted ? ` · range ${fmtPct(anchor.pct_expected_counted.lo90, 0)}–${fmtPct(anchor.pct_expected_counted.hi90, 0)}` : ''}</div>
      </div>
    </div>
    <div class="tiles">
      {#each tiles as t}
        <div class="tile"><div class="tv num">{t.v === null ? '—' : fmtCompact(t.v)}</div><div class="tk">{t.k}</div></div>
      {/each}
      <div class="tile est"><div class="tv num">{left ? `≈${fmtCompact(left.median)}` : '—'}</div><div class="tk">Left to count <Est /></div>{#if left && left.lo !== null && left.hi !== null}<div class="tr num">{fmtCompact(left.lo)}–{fmtCompact(left.hi)}</div>{/if}</div>
    </div>
    {#if statusLine}<p class="status">{statusLine}</p>{/if}
  </section>
  {/if}

  <!-- 2. area picker -->
  {#if ov}
    <section class="section picker"><AreaPicker areas={ov.areas} {used} {area} /></section>
  {:else if waiting}
    <div class="section picker-skel" aria-hidden="true"></div>
  {/if}

  <!-- 3. top races + live analysis (narrow screens: the feed's latest posts come first, the night's narrative) -->
  <section class="section toprow">
    <div>
      <div class="feed-top"><Notes notes={notes.value} {ov} first={2} /></div>
      <div class="section-head"><div><h2>Top races</h2><p class="lede">Charleston County vote. Use the toggle to see the statewide or full-district count.</p></div></div>
      {#if topAll.length}
        <div class="grid-2">{#each top as r (r.race_id)}<HeadToHead race={r} alt={altOf(r)} />{/each}</div>
        {#if otherState.length}
          <h3 class="subhead">Other statewide offices <span class="muted">· Charleston County vote</span></h3>
          <RaceRows races={otherState} />
        {/if}
      {:else if waiting}
        <div class="grid-2"><div class="skel" style="height:514px"></div><div class="skel" style="height:514px"></div></div>
      {:else if !ov}
        <div class="card muted">The races appear here as soon as the first results are published after polls close.</div>
      {:else}
        <div class="card muted">No top races on the ballot for this area.</div>
      {/if}
    </div>
    <aside>
      <div class="feed-side"><Notes notes={notes.value} {ov} /></div>
      {#if !pre && ev.value}<div class="evside"><EarlyVote ev={ev.value} scope="charleston" variant="compact" /></div>{/if}
    </aside>
  </section>

  <!-- 4. map -->
  {#if mapRace && !pre}
    <section class="section card maphero" aria-label="Charleston precinct map">
      <div class="section-head">
        <div><h2>Charleston precinct map</h2><p class="lede">{mapRace.title} — tap a precinct for its result.</p></div>
      </div>
      <div class="chips" role="group" aria-label="Map race">
        {#each mapChoices as r (r.race_id)}<button class="chip" type="button" aria-pressed={r.race_id === mapRace.race_id} onclick={() => (mapRaceId = r.race_id)}>{r.title.replace(/^U\.S\. House District /, 'U.S. House ')}</button>{/each}
      </div>
      <Lazy height={520} onvisible={() => (mapNear = true)}>
        {#if mapFile.value && geo.value && mapFile.value.race_id === mapRace.race_id}
          <ResultsMap race={mapFile.value} geo={geo.value} bind:mode={mapMode} />
        {:else if geo.loading || mapFile.loading}
          <div class="skel" style="height:520px"></div>
        {:else}
          <p class="muted">The precinct map isn’t available.</p>
        {/if}
      </Lazy>
    </section>
  {/if}

  <!-- 5. local races in tabs -->
  {#if tabs.length}
    <section class="section">
      <div class="section-head"><div><h2>Local races</h2><p class="lede">{area ? 'On the ballot in the selected area.' : 'Every contested local race in Charleston County.'}</p></div></div>
      <div class="seg tabs" role="tablist" aria-label="Local races" tabindex="-1" onkeydown={onTabKey}>
        {#each tabs as t (t.id)}
          <button type="button" role="tab" id="tab-{t.id}" aria-controls="panel-local" aria-selected={activeTab?.id === t.id} tabindex={activeTab?.id === t.id ? 0 : -1} onclick={() => (tab = t.id)}>{t.label} <span class="cnt">{t.races.length}</span></button>
        {/each}
      </div>
      {#if activeTab}
        <div class="grid-3 tabgrid" role="tabpanel" id="panel-local" aria-labelledby="tab-{activeTab.id}">{#each activeTab.races as r (r.race_id)}<RaceCard race={r} />{/each}</div>
      {/if}
    </section>
  {/if}

  <!-- unopposed drawer -->
  {#if unopposed.length}
    <section class="section">
      <details class="card unopp">
        <summary><span><b>Unopposed</b> <span class="cnt">{unopposed.length}</span></span><span class="muted small">One candidate per seat — no race to call</span></summary>
        <ul>
          {#each unopposed as r (r.race_id)}
            {@const who = r.choices.filter((c) => !/^write[- ]?in/i.test(c.name))}
            <li><a href={app.href(`#/race/${encodeURIComponent(r.race_id)}`)}><span class="ut">{r.title}</span><span class="uw">{who.map((c) => c.name).join(', ') || '—'}{who[0]?.party && who[0].party !== 'NON' ? ` (${who[0].party.slice(0, 1)})` : ''}</span><span class="uv num">{fmtInt(r.choices.reduce((a, c) => a + c.votes, 0))} votes</span></a></li>
          {/each}
        </ul>
      </details>
    </section>
  {/if}
</div>

<style>
  /* "Vote Vote Vote" → the YTMND page (#/vote) */
  .vvv { display: block; width: fit-content; margin: 20px auto 0; padding: 13px 26px; border-radius: 999px; color: #fff;
    text-decoration: none; text-transform: uppercase; letter-spacing: 0.04em; font: 900 clamp(1.15rem, 4.2vw, 1.6rem)/1
    Impact, Haettenschweiler, 'Arial Narrow Bold', 'Arial Black', var(--font); text-shadow: 2px 2px 0 rgba(0, 0, 0, 0.55);
    background: linear-gradient(90deg, #e63946, #f3a712, #2a9d8f, #3a86ff, #8338ec, #e63946) 0 0 / 300% 100%;
    box-shadow: var(--shadow-lg); animation: vvv-slide 6s linear infinite; transition: transform 0.15s; }
  .vvv:hover { transform: scale(1.05); }
  .vvv span { font-size: 0.8em; }
  @keyframes vvv-slide { to { background-position: 300% 0; } }
  @media (prefers-reduced-motion: reduce) { .vvv { animation: none; } }
  .head { margin-top: 26px; }
  .first { margin-top: 20px; }
  .hero-skel { height: 219px; }
  .picker-skel { height: 87px; }
  @media (max-width: 900px) { .hero-skel { height: 590px; } .picker-skel { height: 108px; } }
  .evside { margin-top: var(--gap); }
  .head h1 { margin-top: 6px; }
  .head.withcd { display: flex; justify-content: space-between; align-items: flex-end; gap: 18px 28px; }
  .head.withcd > div:first-child { min-width: 0; }
  @media (max-width: 900px) { .head.withcd { flex-direction: column; align-items: stretch; } }
  .hero { margin-top: 20px; display: grid; grid-template-columns: auto minmax(0, 1fr); gap: 18px 28px; align-items: center; }
  .ringbox > div { min-width: 0; }
  .tile { min-width: 0; }
  .ringbox { display: flex; gap: 16px; align-items: center; }
  .kline { display: flex; align-items: center; gap: 6px; flex-wrap: wrap; min-width: 0; }
  .kline .kicker { white-space: normal; }
  .subhead { margin: 24px 0 12px; font-size: 1.05rem; }
  .ringtxt { font: 700 1.5rem/1.15 var(--display); margin: 4px 0 2px; }
  .small { font-size: 0.82rem; }
  .tiles { display: grid; grid-template-columns: repeat(5, minmax(0, 1fr)); gap: 10px; }
  .tile { border: 1px solid var(--line); border-radius: 14px; padding: 12px 14px; background: var(--card-2); }
  .tile.est { background: var(--est-bg); border-color: var(--est-line); }
  .tv { font: 700 1.55rem/1.1 var(--display); letter-spacing: -0.01em; }
  .tile.est .tv { color: var(--est-ink); }
  .tk { font-size: 0.78rem; color: var(--ink-3); margin-top: 4px; display: flex; gap: 5px; align-items: center; flex-wrap: wrap; }
  .tr { font-size: 0.74rem; color: var(--est-ink); margin-top: 2px; }
  .status { grid-column: 1 / -1; margin: 0; padding-top: 14px; border-top: 1px solid var(--line); font-weight: 600; color: var(--ink-2); font-size: 0.92rem; }
  .toprow { display: grid; grid-template-columns: minmax(0, 1fr) 320px; gap: var(--gap); align-items: start; }
  .toprow aside { position: sticky; top: 84px; margin-top: 68px; max-height: calc(100vh - 100px); overflow-y: auto; overscroll-behavior: contain; }
  .feed-top { display: none; margin-bottom: var(--gap); }
  .maphero .chips { margin-bottom: 12px; }
  .tabs { margin-bottom: 16px; }
  .cnt { display: inline-grid; place-items: center; min-width: 20px; height: 18px; padding: 0 5px; border-radius: 999px; background: var(--card-3); color: var(--ink-3); font-size: 0.7rem; font-weight: 700; margin-left: 4px; }
  .tabs button[aria-selected='true'] .cnt { background: var(--ink); color: var(--card); }
  .unopp summary { display: flex; justify-content: space-between; align-items: center; gap: 10px; cursor: pointer; list-style: none; font-size: 1.05rem; }
  .unopp summary::-webkit-details-marker { display: none; }
  .unopp ul { list-style: none; margin: 14px 0 0; padding: 0; display: grid; grid-template-columns: repeat(auto-fill, minmax(min(100%, 340px), 1fr)); column-gap: 24px; }
  .unopp li a { display: grid; grid-template-columns: 1fr auto; gap: 0 12px; padding: 10px 4px; border-top: 1px solid var(--line); color: inherit; text-decoration: none; }
  .unopp li a:hover { background: var(--card-2); }
  .ut { font-weight: 600; font-size: 0.9rem; }
  .uw { grid-column: 1; color: var(--ink-3); font-size: 0.84rem; }
  .uv { grid-row: 1 / span 2; grid-column: 2; align-self: center; font-size: 0.8rem; color: var(--ink-3); }
  @media (max-width: 1100px) {
    .toprow { grid-template-columns: minmax(0, 1fr); }
    .toprow aside { position: static; margin-top: 0; }
    .feed-side { display: none; }
    .feed-top { display: block; }
  }
  @media (max-width: 900px) {
    .hero { grid-template-columns: minmax(0, 1fr); }
    .tiles { grid-template-columns: repeat(2, minmax(0, 1fr)); }
    .tiles .tile.est { grid-column: 1 / -1; }
  }
  @media (max-width: 640px) {
    .maphero { margin-left: -14px; margin-right: -14px; border-radius: 0; border-left: 0; border-right: 0; }
  }
</style>
